<template>
  <v-dialog
    v-model="active"
    max-width="500px"
    class="rounded-lg">
    <v-card class="rounded-lg">
      <v-card-title>
        <span
          class="py-1"
          style="font-size: 1.75rem; font-weight: 500;">
          Pick-up Warehouse Form
        </span>
      </v-card-title>
      <v-card-text>
        <validation-observer
          ref="modal-form"
          tag="form"
          @submit.prevent="onSubmit()">
          <v-row>
            <v-col cols="12">
              <validation-provider
                v-slot="{ errors }"
                name="Warehouse"
                rules="required">
                <WarehouseSelect
                  v-model="formData.warehouse"
                  :items="filterWarehouses"
                  item-text="name"
                  item-value="id"
                  :menu-props="{ offsetY: true }"
                  :error-messages="errors"
                  label="Warehouse*"
                  outlined
                  return-object
                  @select="onSelectWarehouse($event)" />
              </validation-provider>
            </v-col>
            <v-col cols="12">
              <gw-autocomplete
                v-model="formData.mode"
                :items="modes"
                label="Mode"
                item-text="text"
                item-value="value"
                :menu-props="{ offsetY: true }"
                color="secondary"
                dense
                outlined
                hide-details />
            </v-col>
            <v-col cols="12">
              <gw-text-field
                v-model="formData.location"
                label="Location*"
                :rules="{
                  required: true,
                  max: 80
                }"
                color="secondary"
                dense
                outlined
                hide-details />
            </v-col>
            <v-col cols="12">
              <v-divider />
            </v-col>
            <v-col cols="12">
              <gw-text-field
                v-model="formData.provinceName"
                label="Province (จังหวัด)*"
                :rules="{
                  required: true,
                  max: 45
                }"
                color="secondary"
                dense
                outlined
                hide-details />
            </v-col>
            <v-col cols="12">
              <gw-text-field
                v-model="formData.cityName"
                label="District (เขต/อำเภอ)*"
                :rules="{
                  required: true,
                  max: 45
                }"
                color="secondary"
                dense
                outlined
                hide-details />
            </v-col>
            <v-col cols="12">
              <gw-text-field
                v-model="formData.districtName"
                label="Sub-district (แขวง/ตำบล)*"
                :rules="{
                  required: true,
                  max: 45
                }"
                color="secondary"
                dense
                outlined
                hide-details />
            </v-col>
            <v-col cols="12">
              <gw-text-field
                v-model="formData.postalCode"
                label="Postal Code*"
                :rules="{
                  required: true,
                  max: 12
                }"
                color="secondary"
                dense
                outlined
                hide-details />
            </v-col>
            <v-col cols="12">
              <gw-textarea
                v-model="formData.detailAddress"
                label="Detail Address (บ้านเลขที่ ถนน)*"
                :rules="{
                  required: true,
                  max: 200
                }"
                color="secondary"
                dense
                outlined
                hide-details />
            </v-col>
            <v-col cols="12">
              <gw-text-field
                v-model="formData.phone"
                label="Phone*"
                :rules="{
                  required: true,
                  max: 70
                }"
                color="secondary"
                dense
                outlined
                hide-details />
            </v-col>
            <v-col
              cols="12"
              class="d-flex justify-end align-center"
              style="gap: 0.5rem;">
              <v-btn
                color="error"
                text
                :disabled="loading"
                @click="active = false">
                ปิด
              </v-btn>
              <v-btn
                type="submit"
                color="success"
                text
                :disabled="loading">
                ยืนยัน
              </v-btn>
            </v-col>
          </v-row>
        </validation-observer>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>

import WarehouseProvider from '@/resources/WarehouseProvider'
import WarehouseSelect from '@/components/WarehouseSelect.vue'

const WarehouseService = new WarehouseProvider()

const defaultFormData = () => ({
  warehouse: null,
  mode: 'schedule',
  location: '',
  provinceName: '',
  cityName: '',
  districtName: '',
  postalCode: '',
  detailAddress: '',
  phone: ''
})

export default {
  components: {
    WarehouseSelect
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    edit: {
      type: Boolean,
      default: false
    },
    data: {
      type: Object,
      default: () => {}
    },
    selectedWarehouses: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      loading: true,
      formData: defaultFormData(),
      modes: [
        { text: 'Schedule', value: 'schedule' },
        { text: 'Daily', value: 'daily' }
      ],
      warehouses: []
    }
  },
  computed: {
    active: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    filterWarehouses () {
      return this.warehouses.filter((warehouse) => !this.selectedWarehouses.find((item) => (
        this.formData.warehouse?.id !== item.id && item.id === warehouse.id
      )))
    }
  },
  watch: {
    active (val) {
      if (!this.data && val) {
        this.formData = defaultFormData()
      }
    },
    data (val) {
      if (val) {
        this.formData = { ...defaultFormData(), ...val }
      }
    }
  },
  mounted () {
    this.getWarehouses()
  },
  methods: {
    onSelectWarehouse (val) {
      if (val) {
        this.formData.location = val.name
        this.formData.provinceName = val.stateProvince
        this.formData.cityName = val.district
        this.formData.districtName = val.subDistrict
        this.formData.postalCode = val.postcode
        this.formData.detailAddress = val.address
      }
    },
    async getWarehouses () {
      try {
        this.loading = true

        const { data } = await WarehouseService.getWarehouse()

        this.warehouses = data
      } catch (error) {
        console.error('getWarehouses', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    async onSubmit () {
      const isValid = await this.$refs['modal-form'].validate()

      if (isValid) {
        this.$emit('submit', this.formData)
        this.active = false
      }
    }
  }
}
</script>
